import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  Box,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { updateSubMember } from '../../controllers/memberManagementController';
import { DIALOG_INPUT_SX, DIALOG_MULTILINE_SX, LabeledControl } from './EditMemberDialog';

const EditSubMemberDialog = ({ open, onClose, subMember, onUpdated }) => {
  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    jobTitle: '',
    company: '',
    sector: '',
    university: '',
    highSchool: '',
    address: '',
    tcKimlikNo: '',
    birthDate: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !subMember) return;
    const full = (subMember.fullName || '').trim();
    const tokens = full.split(/\s+/).filter(Boolean);
    const last = tokens.length > 1 ? tokens[tokens.length - 1] : '';
    const first = tokens.length > 1 ? tokens.slice(0, -1).join(' ') : tokens[0] || '';
    setValues({
      firstName: subMember.firstName || first,
      lastName: subMember.lastName || last,
      email: subMember.email || '',
      phoneNumber: subMember.phoneNumber || '',
      jobTitle: subMember.jobTitle || '',
      company: subMember.company || '',
      sector: subMember.sector || '',
      university: subMember.university || '',
      highSchool: subMember.highSchool || '',
      address: subMember.address || '',
      tcKimlikNo: subMember.tcKimlikNo || '',
      birthDate: subMember.birthDate ? subMember.birthDate.substring(0, 10) : '',
    });
    setError('');
    setLoading(false);
  }, [open, subMember]);

  const handleChange = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const handleClose = () => {
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subMember) return;
    setLoading(true);
    setError('');
    try {
      const updated = await updateSubMember({
        id: subMember.id,
        firstName: values.firstName?.trim(),
        lastName: values.lastName?.trim(),
        phoneNumber: values.phoneNumber,
        email: values.email,
        address: values.address,
        jobTitle: values.jobTitle,
        company: values.company,
        sector: values.sector,
        university: values.university || null,
        highSchool: values.highSchool || null,
        tcKimlikNo: values.tcKimlikNo || null,
        birthDate: values.birthDate ? new Date(values.birthDate).toISOString() : null,
      });
      setLoading(false);
      onUpdated?.(updated);
      handleClose();
    } catch (err) {
      setLoading(false);
      setError(err?.message || 'Yakın güncellenemedi');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (loading) return;
        handleClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Yakını düzenle
        <IconButton
          type="button"
          onClick={handleClose}
          size="small"
          disabled={loading}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <LabeledControl label="Ad">
                  <TextField
                    value={values.firstName}
                    onChange={handleChange('firstName')}
                    required
                    fullWidth
                    disabled={loading}
                    placeholder=""
                    sx={DIALOG_INPUT_SX}
                  />
                </LabeledControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <LabeledControl label="Soyad">
                  <TextField
                    value={values.lastName}
                    onChange={handleChange('lastName')}
                    required
                    fullWidth
                    disabled={loading}
                    placeholder=""
                    sx={DIALOG_INPUT_SX}
                  />
                </LabeledControl>
              </Box>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <LabeledControl label="E-posta">
                  <TextField
                    type="email"
                    value={values.email}
                    onChange={handleChange('email')}
                    fullWidth
                    disabled={loading}
                    autoComplete="email"
                    placeholder=""
                    sx={DIALOG_INPUT_SX}
                  />
                </LabeledControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <LabeledControl label="Telefon">
                  <TextField
                    value={values.phoneNumber}
                    onChange={handleChange('phoneNumber')}
                    fullWidth
                    disabled={loading}
                    placeholder=""
                    sx={DIALOG_INPUT_SX}
                  />
                </LabeledControl>
              </Box>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <LabeledControl label="Ünvan">
                  <TextField
                    value={values.jobTitle}
                    onChange={handleChange('jobTitle')}
                    fullWidth
                    disabled={loading}
                    placeholder=""
                    sx={DIALOG_INPUT_SX}
                  />
                </LabeledControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <LabeledControl label="Şirket">
                  <TextField
                    value={values.company}
                    onChange={handleChange('company')}
                    fullWidth
                    disabled={loading}
                    placeholder=""
                    sx={DIALOG_INPUT_SX}
                  />
                </LabeledControl>
              </Box>
            </Stack>
            <LabeledControl label="Sektör">
              <TextField
                value={values.sector}
                onChange={handleChange('sector')}
                fullWidth
                disabled={loading}
                placeholder=""
                sx={DIALOG_INPUT_SX}
              />
            </LabeledControl>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <LabeledControl label="Üniversite">
                  <TextField
                    value={values.university}
                    onChange={handleChange('university')}
                    fullWidth
                    disabled={loading}
                    placeholder=""
                    sx={DIALOG_INPUT_SX}
                  />
                </LabeledControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <LabeledControl label="Lise">
                  <TextField
                    value={values.highSchool}
                    onChange={handleChange('highSchool')}
                    fullWidth
                    disabled={loading}
                    placeholder=""
                    sx={DIALOG_INPUT_SX}
                  />
                </LabeledControl>
              </Box>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <LabeledControl label="TC Kimlik No">
                  <TextField
                    value={values.tcKimlikNo}
                    onChange={handleChange('tcKimlikNo')}
                    fullWidth
                    disabled={loading}
                    placeholder=""
                    inputProps={{ maxLength: 11 }}
                    sx={DIALOG_INPUT_SX}
                  />
                </LabeledControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <LabeledControl label="Doğum tarihi">
                  <TextField
                    type="date"
                    value={values.birthDate}
                    onChange={handleChange('birthDate')}
                    fullWidth
                    disabled={loading}
                    sx={DIALOG_INPUT_SX}
                  />
                </LabeledControl>
              </Box>
            </Stack>
            <LabeledControl label="Ev Adresi">
              <TextField
                value={values.address}
                onChange={handleChange('address')}
                fullWidth
                multiline
                minRows={2}
                disabled={loading}
                placeholder=""
                sx={DIALOG_MULTILINE_SX}
              />
            </LabeledControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            type="button"
            onClick={handleClose}
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#0a3d7a',
                boxShadow: 'none',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditSubMemberDialog;
